import { Image as ImageIcon, Images, Loader2, Video } from "lucide-react";
import { useId } from "react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";

interface PostFormatToggleProps {
  video: boolean;
  onChange: (video: boolean) => void;
  mediaCount: number;
  label: "Preview" | "Default format" | "Publish as";
  disabled?: boolean;
  loading?: boolean;
  hideLabel?: boolean;
  appearance?: "segmented" | "tabs";
}

export function PostFormatToggle({
  video,
  onChange,
  mediaCount,
  label,
  disabled = false,
  loading = false,
  hideLabel = false,
  appearance = "segmented",
}: PostFormatToggleProps) {
  const labelId = useId();
  const imageLabel = mediaCount > 1 ? "Carousel" : "Image";
  const StaticIcon = mediaCount > 1 ? Images : ImageIcon;
  const segmentClass = (selected: boolean) => appearance === "tabs" ? cn(
    "h-11 gap-2 rounded-none border-b-2 bg-transparent px-2 text-sm shadow-none hover:bg-transparent motion-reduce:transition-none",
    selected ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
  ) : cn(
    "h-11 gap-2 rounded-md border px-3 text-sm motion-reduce:transition-none md:h-8",
    selected
      ? "border-border/60 bg-background text-foreground shadow-sm hover:bg-background"
      : "border-transparent text-muted-foreground hover:bg-background/60 hover:text-foreground",
  );

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <span id={labelId} className={hideLabel ? "sr-only" : "text-xs font-medium text-muted-foreground"}>{label}</span>
      <div role="group" aria-labelledby={labelId} aria-busy={loading}
        className={appearance === "tabs" ? "inline-flex shrink-0 gap-4" : "inline-flex shrink-0 gap-1 rounded-lg bg-muted/70 p-1"}>
        <Button type="button" variant="ghost" className={segmentClass(!video)}
          aria-pressed={!video} disabled={disabled || loading} onClick={() => onChange(false)}>
          <StaticIcon aria-hidden="true" className="h-4 w-4" />
          {imageLabel}
        </Button>
        <Button type="button" variant="ghost" className={segmentClass(video)}
          aria-pressed={video} disabled={disabled || loading} onClick={() => onChange(true)}>
          {loading ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin motion-reduce:animate-none" />
            : <Video aria-hidden="true" className="h-4 w-4" />}
          {/* The first switch builds the choreography on demand (a few seconds) */}
          {loading ? "Preparing video…" : "Video"}
        </Button>
      </div>
      {loading && <span role="status" className="sr-only">Preparing video</span>}
    </div>
  );
}
